import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import FocusLayout from '../components/layouts/FocusLayout';
import { useLanguage } from '../context/LanguageContext';
import { logout, isUserNotFoundError } from '../utils/auth';
import '../styles/Login.css';
import LeftPanel from '../components/full_mock_test/attempt/LeftPanel';
import BottomMenu from '../components/full_mock_test/attempt/BottomMenu';
import RightPanel from '../components/full_mock_test/attempt/RightPanel';
import SectionTransition from '../components/full_mock_test/attempt/SectionTransition';
import FullTestResultSummary from '../components/full_mock_test/attempt/FullTestResultSummary';

// HuPath v2 Design Tokens
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
  plum: '#5B2E5E', cream: '#FFF8E7',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';

const FullTestAttempt = ({ isReviewMode = false }) => {
  const { examId, attemptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const contentRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const mainScrollRef = useRef(null);
  const isInitialized = useRef(false);

  // Determine type based on URL path
  const isPersonalized = location.pathname.includes('personalized-tests');

  // State Management
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState(null);
  const [examTitle, setExamTitle] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');

  const [examStatus, setExamStatus] = useState('loading'); // 'in_progress', 'exam_finished'
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentExerciseData, setCurrentExerciseData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [exerciseLoading, setExerciseLoading] = useState(false);
  const [isFitMode, setIsFitMode] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [fillHintSignal, setFillHintSignal] = useState(0);
  const [sharedColor, setSharedColor] = useState(null);
  const [isGrading, setIsGrading] = useState(false);
  // --- Dynamic Zoom Calculation ---
  useEffect(() => {
    const calculateZoom = () => {
      // Disable zoom for mobile screens (width < 768px)
      if (window.innerWidth < 768) {
        setZoomLevel(1);
        return;
      }

      if (isFitMode && contentRef.current) {
        // We need to wait a bit for the DOM to settle after header hide
        setTimeout(() => {
          if (!contentRef.current) return;
          const container = contentRef.current;
          // Temporarily reset zoom to 1 to get real scrollHeight
          const originalZoom = container.style.zoom;
          container.style.zoom = "1";

          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;

          if (scrollHeight > clientHeight) {
            // Add a small buffer (0.95) to ensure it fits comfortably
            const calculatedZoom = (clientHeight / scrollHeight) * 0.98;
            setZoomLevel(Math.max(0.5, calculatedZoom)); // Don't go below 50%
          } else {
            setZoomLevel(1);
          }
          // Restore zoom
          container.style.zoom = originalZoom;
        }, 100);
      } else {
        setZoomLevel(1);
      }
    };

    calculateZoom();
    window.addEventListener('resize', calculateZoom);
    return () => window.removeEventListener('resize', calculateZoom);
  }, [isFitMode, currentExerciseData]);

  // --- Data Fetching ---
  const fetchExercise = useCallback(async (exerciseId) => {
    setExerciseLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      const mode = isReviewMode ? 'review' : 'test';
      const attemptQuery = isReviewMode && attemptId ? `&attempt_id=${attemptId}` : '';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/client/exercises/${exerciseId}?mode=${mode}${attemptQuery}`, {
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isUserNotFoundError(response, errorData)) {
          logout(navigate);
          return;
        }
        throw new Error(errorData.detail || 'Failed to fetch exercise');
      }
      const data = await response.json();
      setCurrentExerciseData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setExerciseLoading(false);
    }
  }, [isReviewMode, attemptId]);

  const fetchExamDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exams/${examId}`, {
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isUserNotFoundError(response, errorData)) {
          logout(navigate);
          return;
        }
        throw new Error(errorData.detail || 'Failed to fetch exam details');
      }
      const data = await response.json();
      setExamTitle(data.title);
    } catch (err) {
      setError(err.message);
    }
  }, [examId]);

  const handleAttemptData = useCallback((data) => {
    // If it's personalized test, normalize data to have an exam_structure
    if (isPersonalized && data.test_detail && !data.exam_structure) {
      data.title = data.test_detail.title;
      data.exam_structure = [{
        section_id: 'personalized',
        section_title: 'Personalized Test',
        total_duration_minutes: 0, // No timer
        exercises: data.test_detail.items.sort((a, b) => a.order_index - b.order_index).map(item => ({
          exercise_id: item.exercise.id,
          ...item.exercise
        }))
      }];
    }

    if (data.exam_structure) {
      data.exam_structure.sort((a, b) => a.order_index - b.order_index);
    }
    setAttempt(data);
    if (data.title) setExamTitle(data.title);
    const firstExerciseId = data.exam_structure && data.exam_structure[0]?.exercises[0]?.exercise_id;
    if (firstExerciseId) {
      fetchExercise(firstExerciseId);
    }
    setExamStatus('in_progress');
  }, [fetchExercise, isPersonalized]);

  const getExistingAttempt = useCallback(async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found. Please log in.');

      let url = `${process.env.REACT_APP_API_URL}/exam-attempts/${id}`;
      if (isPersonalized) {
        url = `${process.env.REACT_APP_API_URL}/client/personalized-tests/attempts/${id}/result`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isUserNotFoundError(response, errorData)) {
          logout(navigate);
          return;
        }
        throw new Error(errorData.detail || 'Failed to fetch existing exam attempt');
      }
      const data = await response.json();
      handleAttemptData(data);
    } catch (err) {
      setError(err.message);
    }
  }, [handleAttemptData, isPersonalized]);

  const createAttempt = useCallback(async (forceNew) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found. Please log in.');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exams/${examId}/attempts`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force_new: forceNew })
      });
      if (response.status === 409) {
        const errorData = await response.json();
        getExistingAttempt(errorData.detail.attempt_id);
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isUserNotFoundError(response, errorData)) {
          logout(navigate);
          return;
        }
        throw new Error(errorData.detail || 'Failed to create exam attempt');
      }
      const data = await response.json();
      handleAttemptData(data);
    } catch (err) {
      setError(err.message);
    }
  }, [examId, handleAttemptData]);

  // --- Effects ---
  useEffect(() => {
    if (isInitialized.current) return;

    if (examId) fetchExamDetails();
    if (attemptId) {
      isInitialized.current = true;
      getExistingAttempt(attemptId);
    } else if (examId) {
      isInitialized.current = true;
      createAttempt(false);
    }
  }, [attemptId, examId, createAttempt, getExistingAttempt, fetchExamDetails]);

  // Timer and Section Title effect
  useEffect(() => {
    if (attempt) {
      const currentSection = attempt.exam_structure[currentSectionIndex];
      if (currentSection) {
        if (!isReviewMode) {
          setTimeLeft(currentSection.total_duration_minutes * 60);
        } else {
          setTimeLeft(null);
        }
        setSectionTitle(currentSection.section_title);
      } else {
        setTimeLeft(null);
        setSectionTitle('');
      }
    }
  }, [attempt, currentSectionIndex, isReviewMode]);

  // Countdown effect
  useEffect(() => {
    if (isReviewMode || timeLeft === null || timeLeft <= 0 || examStatus !== 'in_progress') {
      return;
    }
    const timerId = setInterval(() => setTimeLeft(time => time - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, isReviewMode, examStatus]);

  // Scroll to top when exercise changes
  useEffect(() => {
    if (currentExerciseData) {
      // Use a small timeout to ensure DOM has updated and layout settled
      const timeoutId = setTimeout(() => {
        if (mainScrollRef.current) mainScrollRef.current.scrollTo({ top: 0, behavior: 'instant' });
        if (leftPanelRef.current) leftPanelRef.current.scrollTo({ top: 0, behavior: 'instant' });
        if (rightPanelRef.current) rightPanelRef.current.scrollTo({ top: 0, behavior: 'instant' });
        window.scrollTo({ top: 0, behavior: 'instant' });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [currentExerciseData]);

  const handleNextExercise = () => {
    setShowHint(false);
    setFillHintSignal(0); // Reset so new exercise doesn't auto-fill
    setSharedColor(null); // Reset shared color for Part 5
    if (!attempt) return;
    const currentSection = attempt.exam_structure[currentSectionIndex];
    if (currentExerciseIndex < currentSection.exercises.length - 1) {
      const nextExerciseIndex = currentExerciseIndex + 1;
      setCurrentExerciseIndex(nextExerciseIndex);
      fetchExercise(currentSection.exercises[nextExerciseIndex].exercise_id);
    } else {
      setShowSectionModal(true); // End of section, show modal
    }
  };

  const handlePrevExercise = () => {
    setShowHint(false);
    setFillHintSignal(0); // Reset so new exercise doesn't auto-fill
    setSharedColor(null); // Reset shared color for Part 5
    if (!attempt) return;
    if (isReviewMode) {
      if (currentExerciseIndex > 0) {
        const prevExerciseIndex = currentExerciseIndex - 1;
        const prevExerciseId = attempt.exam_structure[currentSectionIndex].exercises[prevExerciseIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentExerciseIndex(prevExerciseIndex);
      } else if (currentSectionIndex > 0) {
        const prevSectionIndex = currentSectionIndex - 1;
        const prevSection = attempt.exam_structure[prevSectionIndex];
        const lastExerciseInPrevSectionIndex = prevSection.exercises.length - 1;
        const prevExerciseId = prevSection.exercises[lastExerciseInPrevSectionIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentSectionIndex(prevSectionIndex);
        setCurrentExerciseIndex(lastExerciseInPrevSectionIndex);
      }
    } else {
      if (currentExerciseIndex > 0) {
        const prevExerciseIndex = currentExerciseIndex - 1;
        const prevExerciseId = attempt.exam_structure[currentSectionIndex].exercises[prevExerciseIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentExerciseIndex(prevExerciseIndex);
      }
    }
  };

  const handleFillHint = () => {
    setFillHintSignal(prev => prev + 1);
  };

  const handleReviewSection = () => {
    setShowSectionModal(false);
  };

  const saveSectionAnswers = async () => {
    if (!attempt) return;
    const currentSection = attempt.exam_structure[currentSectionIndex];
    const attemptKey = `attempt_${attempt.attempt_id}`;
    let allSavedAnswers = {};

    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allSavedAnswers = JSON.parse(savedAttempt);
    } catch (e) { console.error("Failed to parse localStorage data:", e); }

    const answersToSubmit = [];
    currentSection.exercises.forEach(exercise => {
      const exerciseAnswers = allSavedAnswers[exercise.exercise_id];
      if (exerciseAnswers) {
        Object.keys(exerciseAnswers).forEach(questionId => {
          answersToSubmit.push({
            question_id: questionId,
            user_answer: [JSON.stringify(exerciseAnswers[questionId])]
          });
        });
      }
    });

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attempt.attempt_id}/answers`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: answersToSubmit, exam_section_id: currentSection.section_id })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isUserNotFoundError(response, errorData)) {
          logout(navigate);
          return;
        }
      }
      // Clear section answers from local storage after submission
      currentSection.exercises.forEach(exercise => { delete allSavedAnswers[exercise.exercise_id]; });
      localStorage.setItem(attemptKey, JSON.stringify(allSavedAnswers));
    } catch (err) {
      setError(err.message);
    }
  };

  const proceedToNextSection = () => {
    if (!attempt) return;
    setSharedColor(null); // Reset shared color
    setShowSectionModal(false); // Close modal
    if (currentSectionIndex < attempt.exam_structure.length - 1) {
      const nextSectionIndex = currentSectionIndex + 1;
      const nextSection = attempt.exam_structure[nextSectionIndex];

      if (nextSection && nextSection.exercises && nextSection.exercises.length > 0) {
        setCurrentSectionIndex(nextSectionIndex);
        setCurrentExerciseIndex(0);
        fetchExercise(nextSection.exercises[0].exercise_id);
        setExamStatus('in_progress');
      } else {
        // Nếu section tiếp theo rỗng, thử nhảy tiếp hoặc báo lỗi
        console.warn("Next section is empty:", nextSection);
        setCurrentSectionIndex(nextSectionIndex); // Vẫn chuyển index để tránh loop
        // Có thể gọi đệ quy proceedToNextSection hoặc xử lý kết thúc
      }
    } else {
      if (isReviewMode) {
        if (isPersonalized) {
          navigate('/personalized-tests');
        } else {
          navigate(`/full-mock-test/result/${attempt.attempt_id}`);
        }
      } else {
        // Final section completed - submit exam directly
        submitExamAttempt();
      }
    }
  };

  const handleNextSection = async () => {
    if (!isReviewMode) {
      await saveSectionAnswers();
    }
    proceedToNextSection();
  };

  const submitExamAttempt = async () => {
    if (!attempt) return;
    setIsGrading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attempt.attempt_id}/submit`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: ''
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isUserNotFoundError(response, errorData)) {
          logout(navigate);
          return;
        }
        throw new Error(errorData.detail || 'Failed to submit exam attempt');
      }
      const data = await response.json();
      localStorage.removeItem(`attempt_${attempt.attempt_id}`);
      navigate(`/full-mock-test/result/${attempt.attempt_id}`, { state: { result: data } });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGrading(false);
    }
  };

  // --- Render Logic ---
  if (error) return <div className="error-message">{t('error')}: {error}</div>;

  return (
    <>
      {isGrading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
            <div style={{
              width: 88, height: 88, borderRadius: 24,
              background: HT.greenBg, border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 48, color: HT.teal }}>assignment_turned_in</span>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text, marginBottom: 12 }}>
              AI đang chấm điểm bài viết của bạn…
            </h2>
            <p style={{ fontSize: 15, color: HT.textSoft, marginBottom: 24, fontFamily: FONT_BODY }}>
              (Có thể mất vài giây)
            </p>
            <div style={{
              width: '100%', background: HT.cream, borderRadius: 99,
              height: 8, overflow: 'hidden', position: 'relative',
              border: `1px solid ${HT.border}`,
            }}>
              <div className="animate-loading-bar" style={{
                position: 'absolute', top: 0, bottom: 0,
                background: HT.teal, borderRadius: 99, width: '60%',
              }} />
            </div>
          </div>
        </div>
      )}
      {/* Conditional rendering based on attempt and examStatus */}
      {!attempt ? (
        <div className="exam-attempt-container"><p>{t('loading')}</p></div>
      ) : currentExerciseData ? (
        <FocusLayout>
          {/* ===== FOCUS SPLIT TOP BAR — HuPath v2 ===== */}
          <header
            className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-3 md:px-6 justify-between"
            style={{
              background: HT.yellowBg,
              borderBottom: `1px solid ${HT.border}`,
              fontFamily: FONT_BODY,
            }}
          >
            <div className="flex items-center gap-2 md:gap-3 overflow-hidden">
              <button
                onClick={() => navigate(-1)}
                style={{
                  width: 36, height: 36, borderRadius: 999,
                  background: HT.white, border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: 14, fontWeight: 800, color: HT.text,
                }}
                aria-label="Exit"
                title={t('exit')}
              >✕</button>
              <div
                style={{
                  background: HT.white, border: `1px solid ${HT.border}`,
                  padding: '5px 12px', borderRadius: 999,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 800, color: HT.plum, letterSpacing: 1.2 }}>
                  {t('part').toUpperCase()}
                </span>
                <span style={{ width: 2, height: 14, background: HT.border, opacity: 0.5 }} />
                <span style={{ fontSize: 13, fontWeight: 800, color: HT.text }}>
                  {currentSectionIndex + 1}/{attempt.exam_structure.length}
                </span>
              </div>
              <div
                className="hidden md:flex"
                style={{
                  background: HT.yellow, boxShadow: `0 2px 0 ${HT.yellowShadow}`,
                  padding: '5px 12px', borderRadius: 999,
                  fontSize: 12, fontWeight: 800, color: HT.text, letterSpacing: 0.3,
                  textTransform: 'uppercase', maxWidth: 240,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >{sectionTitle}</div>
            </div>

            <div className="hidden md:flex flex-1 items-center justify-center gap-3 px-4">
              <span style={{ fontSize: 11, fontWeight: 800, color: HT.textSoft, letterSpacing: 0.8 }}>
                {t('question').toUpperCase()} {currentExerciseIndex + 1}/{attempt.exam_structure[currentSectionIndex]?.exercises?.length || 0}
              </span>
              <div style={{
                flex: 1, maxWidth: 220, height: 14, background: HT.greenBg,
                borderRadius: 999,
                overflow: 'hidden', padding: 2, position: 'relative',
              }}>
                <div style={{
                  width: `${((currentExerciseIndex + 1) / (attempt.exam_structure[currentSectionIndex]?.exercises?.length || 1)) * 100}%`,
                  height: '100%', background: HT.teal, borderRadius: 999,
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {!isReviewMode && (
                <>
                  <button
                    onClick={() => setShowHint(!showHint)}
                    title={showHint ? t('hideHint') : t('showHint')}
                    style={{
                      width: 36, height: 36, borderRadius: 999,
                      background: showHint ? HT.yellow : HT.white,
                      border: `1px solid ${HT.border}`,
                      boxShadow: SHADOW_SM,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: HT.text,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>lightbulb</span>
                  </button>
                  <button
                    onClick={handleFillHint}
                    title={t('fillCorrectAnswer')}
                    style={{
                      width: 36, height: 36, borderRadius: 999,
                      background: HT.white, border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: HT.plum,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>auto_fix_high</span>
                  </button>
                </>
              )}
              {timeLeft !== null && (
                <div
                  style={{
                    background: timeLeft <= 60 ? HT.coralDark : HT.text, color: HT.white,
                    padding: '6px 12px', borderRadius: 999,
                    fontSize: 13, fontWeight: 800,
                    display: 'flex', alignItems: 'center', gap: 6,
                    minWidth: 78, justifyContent: 'center',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>timer</span>
                  {Math.floor(timeLeft / 60).toString().padStart(2, '0')}:{(timeLeft % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>
          </header>

          {/* ===== FOCUS SPLIT MAIN ===== */}
          <div
            ref={mainScrollRef}
            className="flex flex-col md:flex-row md:h-screen pt-14 pb-20 md:pb-16 overflow-y-auto md:overflow-hidden"
            style={{ background: HT.yellowBg, fontFamily: FONT_BODY }}
          >

            {/* ===== LEFT PANEL (45%) — Sidebar content ===== */}
            <div
              className="flex flex-col w-full md:w-[45%] md:h-full z-10"
              style={{ background: HT.yellowBg }}
            >
              <div
                ref={leftPanelRef}
                className="flex-1 min-h-0 overflow-y-auto px-2 md:px-6 py-4 md:py-6"
                style={{ position: 'relative', zIndex: 1 }}
              >
                <LeftPanel
                    examTitle={examTitle}
                    sectionTitle={sectionTitle}
                    timeLeft={timeLeft}
                    audioUrl={currentExerciseData?.content_data?.audio_url}
                    isStrictAudio={false}
                    partLabel={currentExerciseData?.content_data?.part_label}
                    questionCountLabel={currentExerciseData?.content_data?.question_count_label}
                    instruction={currentExerciseData?.content_data?.instruction}
                    storyText={currentExerciseData?.content_data?.story_text}
                    imageUrl={
                      ['reading_writing_flyer_part_5', 'listen_flyer_part_2', 'reading_writing_movers_part_2'].includes(currentExerciseData?.type)
                        ? currentExerciseData?.content_data?.image_url
                        : null
                    }
                    sharedOptions={['listen_flyer_part_3', 'listen_movers_part_3', 'listen_mover_part_3', 'reading_writing_movers_part_1', 'reading_writing_movers_part_3'].includes(currentExerciseData?.type) ? currentExerciseData?.content_data?.shared_options : null}
                    colorOptions={['listen_flyer_part_5', 'listen_mover_part_5', 'listen_movers_part_5', 'listen_starters_part_4'].includes(currentExerciseData?.type) ? currentExerciseData?.questions?.filter(q => !q.question_text?.toLowerCase().startsWith('input')).map(q => q.question_text) : null}
                    inputQuestions={['listen_flyer_part_5', 'listen_mover_part_5', 'listen_movers_part_5', 'listen_starters_part_4'].includes(currentExerciseData?.type) ? currentExerciseData?.questions?.filter(q => q.question_text?.toLowerCase().startsWith('input')) : null}
                    optionBank={
                      currentExerciseData?.type === 'reading_writing_flyer_part_4' ? currentExerciseData?.content_data?.option_bank
                        : currentExerciseData?.type === 'reading_writing_movers_part_4' ? [...(currentExerciseData?.questions || [])].sort((a, b) => a.order_index - b.order_index).map(q => ({ no: q.order_index, options: q.options || [] }))
                        : null
                    }
                    optionBankExampleAnswers={
                      ['reading_writing_flyer_part_4', 'reading_writing_movers_part_4'].includes(currentExerciseData?.type) ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                        : null
                    }
                    wordBank={
                      currentExerciseData?.type === 'reading_writing_flyer_part_1' ? currentExerciseData?.content_data?.word_bank
                        : currentExerciseData?.type === 'reading_writing_flyer_part_3' ? currentExerciseData?.content_data?.word_bank
                          : null
                    }
                    wordBankExampleAnswers={
                      currentExerciseData?.type === 'reading_writing_flyer_part_1' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                        : currentExerciseData?.type === 'reading_writing_flyer_part_3' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                          : null
                    }
                    answerBank={currentExerciseData?.type === 'reading_writing_flyer_part_2' ? currentExerciseData?.content_data?.answer_bank : null}
                    answerBankExampleAnswers={currentExerciseData?.type === 'reading_writing_flyer_part_2' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers : null}
                    sharedColor={sharedColor}
                    setSharedColor={setSharedColor}
                  />
                </div>
              </div>

            {/* ===== RIGHT PANEL (55%) — Exercise content ===== */}
            <div
              className="flex flex-col w-full md:w-[55%] md:h-full relative"
              style={{ background: HT.yellowBg }}
            >
              <div
                ref={contentRef}
                className="flex-1 md:overflow-hidden flex flex-col items-center justify-center p-2 md:px-8 md:py-6"
                style={isFitMode && zoomLevel !== 1 ? { zoom: zoomLevel, position: 'relative', zIndex: 1 } : { position: 'relative', zIndex: 1 }}
              >
                {exerciseLoading ? (
                  <div className="flex items-center justify-center h-48 md:h-full w-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4" style={{ borderBottomColor: HT.coralDark }}></div>
                  </div>
                ) : (
                  <div
                    ref={rightPanelRef}
                    className={`w-full flex-1 ${currentExerciseData?.type?.includes('listen') ? 'md:overflow-hidden flex flex-col min-h-0' : 'md:overflow-y-auto custom-scrollbar'}`}
                    style={{ background: 'transparent' }}
                  >
                    <RightPanel
                      exercise={currentExerciseData}
                      attemptId={attempt.attempt_id}
                      isReviewMode={isReviewMode}
                      showHint={showHint}
                      fillHintSignal={fillHintSignal}
                      isFitMode={isFitMode}
                      zoomLevel={zoomLevel}
                      sharedColor={sharedColor}
                      setSharedColor={setSharedColor}
                    />
                  </div>
                )}
              </div>

              {/* Mobile BottomMenu - Fixed at bottom */}
              <div
                className="md:hidden fixed bottom-0 left-0 right-0 z-50 h-16 flex items-center"
                style={{ background: HT.white, borderTop: `1px solid ${HT.border}` }}
              >
                <BottomMenu
                  onPrev={handlePrevExercise}
                  onNext={handleNextExercise}
                  onToggleHint={() => setShowHint(!showHint)}
                  onFillHint={handleFillHint}
                  showHint={showHint}
                  isReviewMode={isReviewMode}
                  currentIndex={currentExerciseIndex}
                  totalCount={attempt.exam_structure[currentSectionIndex]?.exercises?.length || 1}
                />
              </div>
            </div>
          </div>

          {/* ===== FOCUS SPLIT BOTTOM BAR — HuPath v2 ===== */}
          <footer
            className="fixed bottom-0 left-0 right-0 z-50 h-16 hidden md:flex items-center"
            style={{ background: HT.white, borderTop: `1px solid ${HT.border}` }}
          >
            <BottomMenu
              onPrev={handlePrevExercise}
              onNext={handleNextExercise}
              onToggleHint={() => setShowHint(!showHint)}
              onFillHint={handleFillHint}
              showHint={showHint}
              isReviewMode={isReviewMode}
              hideHint={true}
              currentIndex={currentExerciseIndex}
              totalCount={attempt.exam_structure[currentSectionIndex]?.exercises?.length || 1}
            />
          </footer>

          <SectionTransition
            isOpen={showSectionModal}
            sectionTitle={attempt.exam_structure[currentSectionIndex].section_title}
            onReview={handleReviewSection}
            onNext={handleNextSection}
            isLastSection={currentSectionIndex === attempt.exam_structure.length - 1}
            isReviewMode={isReviewMode}
          />
        </FocusLayout>
      ) : null}
      <style>{`
        @keyframes loading-bar {
            0% { left: -100%; width: 100%; }
            100% { left: 100%; width: 100%; }
        }
        .animate-loading-bar {
            animation: loading-bar 2s infinite linear;
        }
      `}</style>
    </>
  );
};

export default FullTestAttempt;
